import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Button, InformationalScreen, LoadingModal, Stack, Text, } from '@adc/parallax-component-library';
import { APP_RETURN_TIMEOUT, ApiStatus, ErrorCode } from 'Constants';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useApiError, useAuth } from 'Hooks';
import { Banner, Footer, FormContainer, Input, InputPassword, MainContainer, TopHeaderBackArrow, } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { notifyApp } from 'Utilities/appCommunicator';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
import { signIn } from 'Services/auth';
const initialValues = {
    email: '',
    password: '',
};
const mapStateToProps = ({ env: { deviceId, osType, osVersion, app, appVersion, appHash }, nav: { redirection }, }) => {
    return {
        deviceId,
        osType,
        osVersion,
        app,
        appVersion,
        redirection,
        appHash,
    };
};
const connector = connect(mapStateToProps);
const Login = ({ deviceId, osType, osVersion, app, appVersion, appHash }) => {
    const [loading, setLoading] = useState(false);
    const { showApiErrorModal, showNetworkErrorModal } = useApiError();
    const { handleLoginError, handleLockoutError, showHcpError, handleAndroidIntegrityError, isLocked, lockoutMessage, lockoutSubmessage, showSessionManagementError, showCountryRegionError, showCountryNotSupportedError, } = useAuth('login');
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'))
            .required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
        password: Yup.string().required(i18n.t('Login.content.signInForm.formField.password.errors.required')),
    });
    const handleApiRequestError = useCallback((error, resetForm, signInRequest) => {
        const data = error.response?.data
            ? error.response.data
            : {
                details: error.code === ErrorCode.NETWORK_ERROR
                    ? ErrorCode.NETWORK_ERROR
                    : i18n.t('Global.modals.errorCommunicatingServer.body'),
            };
        notifyApp('adc-webview:signin', { status: error.status, data });
        switch (error?.response?.status) {
            case ApiStatus.UNAUTHORIZED: {
                const code = error.response?.data.code;
                if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
                    showHcpError();
                    return;
                }
                // Disable Android Integrity for release/3.23.1
                // if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
                //   handleAndroidIntegrityError(error);
                //   return;
                // }
                if (code === ApiStatus.SESSION_MANAGEMENT_ERROR) {
                    showSessionManagementError(signInRequest);
                    return;
                }
                handleLoginError(error, resetForm);
                break;
            }
            case ApiStatus.TOO_MANY_REQUESTS: {
                handleLockoutError(error);
                resetForm();
                break;
            }
            case ApiStatus.BAD_REQUEST: {
                const code = error.response?.data.code;
                // Separate the error handling for LLU5 specifically, remove this if statement is Libre will follow the same error handling
                if (app === 'llu5') {
                    if (code === ApiStatus.WRONG_REGION) {
                        showCountryRegionError();
                        return;
                    }
                    if (code === ApiStatus.UNSUPPORTED_COUNTRY) {
                        showCountryNotSupportedError();
                        return;
                    }
                }
                // Remove this if the above is updated for Libre in addition to LLU5
                if (code === ApiStatus.UNSUPPORTED_COUNTRY || code === ApiStatus.WRONG_REGION) {
                    showCountryRegionError();
                    return;
                }
                if (code === ApiStatus.ANDROID_INTEGRITY_ERROR) {
                    handleAndroidIntegrityError(error);
                    return;
                }
                showApiErrorModal();
                break;
            }
            default:
                error.code === ErrorCode.NETWORK_ERROR ? showNetworkErrorModal() : showApiErrorModal();
        }
    }, [
        showNetworkErrorModal,
        showApiErrorModal,
        handleLoginError,
        showHcpError,
        handleAndroidIntegrityError,
        showSessionManagementError,
        handleLockoutError,
        app,
        showCountryRegionError,
        showCountryNotSupportedError,
    ]);
    const signInRequest = useCallback(async (formValues, resetForm, force) => {
        try {
            setLoading(true);
            const { email, password } = formValues;
            const authSession = await signIn(email, password.trim(), osType, osVersion, app, appVersion, deviceId, appHash, force);
            store.dispatch({ type: AUTH_SESSION, authSession });
            if (app !== 'llu5') {
                if (Object.hasOwn(authSession.include.patient.consents, 'hipaa') &&
                    !authSession.include.patient.consents?.hipaa?.at) {
                    mediator.publish('router:navigate', '/hipaa');
                    return;
                }
                if (!authSession.include.patient.consents?.realWorldEvidence?.at) {
                    mediator.publish('router:navigate', '/rwe-consent');
                    return;
                }
            }
            notifyApp('adc-webview:signin', authSession);
            // This timeout is added to give the app time to redirect the user back.
            setTimeout(() => {
                setLoading(false);
            }, APP_RETURN_TIMEOUT);
        }
        catch (err) {
            setLoading(false);
            const error = err;
            handleApiRequestError(error, resetForm, () => signInRequest(formValues, resetForm, true));
        }
    }, [app, appVersion, deviceId, handleApiRequestError, osType, osVersion, appHash]);
    const onSubmit = useCallback(async (values, { setSubmitting, resetForm }) => {
        signInRequest(values, resetForm).finally(() => {
            setSubmitting(false);
        });
    }, [signInRequest]);
    const handleClickForgotPass = useCallback(() => {
        mediator.publish('router:navigate', '/forgot-password');
    }, []);
    const handleClickCreateAccount = useCallback(() => {
        /*
          Need to use the redirect method to replaces the current location in browser history.
          When the user clicks the back button on the create account page, it should redirect the user back to the app.
        */
        mediator.publish('router:redirect', '/create-account');
    }, []);
    const { handleSubmit, handleChange, handleBlur, isValid, errors, touched, values } = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
        validateOnBlur: true,
        initialErrors: {
            email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
            password: i18n.t('Login.content.signInForm.formField.email.errors.required'),
        },
    });
    const appName = i18n.t('Global.appNames.libreView');
    return (React.createElement(MainContainer, null,
        loading && React.createElement(LoadingModal, { "aria-label": "Loading" }),
        React.createElement(InformationalScreen, { topNavigationProps: {
                animatedTitle: true,
                title: i18n.t('Login.title'),
                buttonStart: {
                    element: React.createElement(TopHeaderBackArrow, { testID: "Auth.action-go-back" }),
                    label: 'Go back', // TODO: temporary, no "back button" in translations found
                    onPress: () => notifyApp('adc-webview:navigate-back-app'),
                },
            }, testID: "Auth" },
            React.createElement(InformationalScreen.Page, null,
                React.createElement(InformationalScreen.Content, null,
                    React.createElement(Text, { testID: "Auth.useFreestyleAccount", nativeID: "Auth.useFreestyleAccount", marginTop: -8 }, i18n.t('Login.subtitle', { appName })),
                    lockoutMessage ? (React.createElement(Banner, { testID: "Login", message: lockoutMessage, submessage: lockoutSubmessage })) : null,
                    React.createElement(FormContainer, null,
                        React.createElement(Input, { "aria-label": i18n.t('Login.content.signInForm.formField.email.label'), placeholder: i18n.t('Login.content.signInForm.formField.email.placeholder'), label: i18n.t('Login.content.signInForm.formField.email.label'), errorMessage: errors.email, isInvalid: errors.email && touched.email ? true : false, value: values.email, testID: "email", onChangeText: handleChange('email'), onBlur: handleBlur('email'), inputMode: "email", disabled: isLocked }),
                        React.createElement(InputPassword, { placeholder: i18n.t('Login.content.signInForm.formField.password.placeholder'), label: i18n.t('Login.content.signInForm.formField.password.label'), isInvalid: errors.password && touched.password ? true : false, errorMessage: errors.password, testID: "password", onBlur: handleBlur('password'), onChangeText: handleChange('password'), disabled: isLocked, value: values.password }),
                        React.createElement(Stack, { flexDirection: "column", alignItems: "center" },
                            React.createElement(Button, { variant: "tertiary", label: i18n.t('Login.content.signInForm.secondaryText'), onPress: handleClickForgotPass, testID: "Auth.forgotPassword" })))),
                React.createElement(InformationalScreen.Footer, null,
                    React.createElement(Footer, { buttonLabel: i18n.t('Login.content.footerCreateAccount.links.signIn.label'), onButtonPress: handleSubmit, buttonTestID: "Login.signIn-btn", secondarySectionLabel: i18n.t('Login.content.footerCreateAccount.primaryText'), onSecondaryButtonPress: handleClickCreateAccount, secondaryButtonTestID: "Login.createAccount-btn", secondaryButtonLabel: i18n.t('Login.content.footerCreateAccount.links.createAccount.label'), disabled: isLocked || !isValid }))))));
};
export default connector(Login);
